/* eslint-disable import/no-unused-modules */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { translate } from 'helpers/translateHelper';
import ErrorView from './ErrorView';
import { LoadingIndicator } from 'sharedComponents';
import { suppressReactErrorOverlay } from './errorOverlaySuppress';
import './_style.scss';

const RetryWrapper = ({ children, onMount }) => {
    React.useEffect(() => {
        if (typeof onMount === 'function') { onMount(); }
    }, []);

    return children;
};

class ErrorBoundaryComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            errorCode: null,
            errorMessage: '',
            errorStack: null,
            originalError: null,
            retryKey: 0,
            isRetrying: false
        };

        this.expectedChildren = 0;
        this._retryReadyIds = new Set();

        // Handle unhandled promise rejections
        window.addEventListener('unhandledrejection', this.handleUnhandledRejection);

        // Handle global errors
        window.addEventListener('error', this.handleGlobalError);

        // Override console.error to suppress React error overlay in development
        if (process.env.NODE_ENV === 'development') {
            this.originalConsoleError = console.error;
            console.error = (...args) => {
                // Check if this is a React error overlay message
                const message = args[0];
                if (typeof message === 'string' &&
                    (message.includes('The above error occurred in the') ||
                        message.includes('React will try to recreate'))) {
                    return; // Suppress React error overlay messages
                }
                this.originalConsoleError.apply(console, args);
            };

            // Initialize error overlay suppression
            suppressReactErrorOverlay();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
        window.removeEventListener('error', this.handleGlobalError);

        // Restore original handlers
        if (this.originalConsoleError) {
            console.error = this.originalConsoleError;
        }
    }

    handleUnhandledRejection = (event) => {
        // Prevent the default browser error handling
        event.preventDefault();

        // Check if this is a 401 error from API call
        const errorCode = event.reason?.response?.status || 500;
        if (errorCode === 401) {
            // Redirect to login for 401 errors
            window.location.href = '/login';
            return;
        }

        // ignore 417 errors
        if (errorCode === 417) {
            return;
        }

        this.setState({
            hasError: true,
            errorCode: errorCode,
            errorMessage: translate('ABB.Powertrain.Frontend.errorBoundaryMessageDescAsync'),
            errorStack: event.reason?.stack || 'No stack trace available',
            originalError: null
        });
    };

    handleGlobalError = (event) => {
        // Prevent the default browser error handling
        event.preventDefault();

        // Check if this is a 401 error from API call
        const errorCode = event.error?.response?.status || 500;
        if (errorCode === 401) {
            // Redirect to login for 401 errors
            window.location.href = '/login';
            return;
        }

        // ignore 417 errors
        if (errorCode === 417) {
            return;
        }

        this.setState({
            hasError: true,
            errorCode: errorCode,
            errorMessage: event.error?.message || translate('ABB.Powertrain.Frontend.errorBoundaryMessageDescUnknown'),
            errorStack: event.error?.stack || 'No stack trace available',
            originalError: null
        });
    };

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        let errorCode = 500;
        const errorMessage = null; // We'll translate this in the render method
        let originalError = null;
        const errorStack = error?.stack || 'No stack trace available';

        // Check if error has a status code property (for API errors)
        if (error?.response?.status) {
            errorCode = error.response.status;

            // For 401 errors, we should redirect to login instead of showing error boundary
            if (errorCode === 401 && typeof window !== 'undefined') {
                // Handle 401 by redirecting to login page
                setTimeout(() => {
                    window.location.href = '/login';
                }, 100);
                return { hasError: false, errorCode: null, errorMessage: null, errorStack: null, originalError: null };
            }

            // ignore 417 errors
            if (errorCode === 417) {
                return { hasError: false, errorCode: null, errorMessage: null, errorStack: null, originalError: null };
            }
        } else if (error?.message) {
            // Store the original error to translate later
            originalError = error.message;
        }

        return { hasError: true, errorCode, errorMessage, errorStack, originalError };
    }

    componentDidCatch(error, errorInfo) {
        // You can log the error to an error reporting service here
        console.error('Error caught by ErrorBoundary:', error, errorInfo);

        // Hide React error overlay in development mode
        this.hideReactErrorOverlay();

        // Update state with additional error info
        this.setState(prevState => ({
            ...prevState,
            errorStack: error?.stack || 'No stack trace available'
        }));
    }

    hideReactErrorOverlay = () => {
        if (process.env.NODE_ENV === 'development') {
            // Hide the React error overlay
            setTimeout(() => {
                const errorOverlay = document.querySelector('iframe[src*="react-error-overlay"]') ||
                    document.querySelector('[data-react-error-overlay]') ||
                    document.querySelector('div[style*="position: fixed"][style*="z-index: 999999"]');

                if (errorOverlay) {
                    errorOverlay.style.display = 'none';
                }

                // Also try to hide parent containers
                const errorContainer = document.querySelector('div[style*="position: fixed"][style*="top: 0"][style*="left: 0"]');
                if (errorContainer?.innerHTML?.includes('Error')) {
                    errorContainer.style.display = 'none';
                }
            }, 100);
        }
    };

    handleRetry = () => {
        this.expectedChildren = React.Children.count(this.props.children) || 0;
        this._retryReadyIds.clear();

        this.setState(prevState => ({
            hasError: false,
            errorCode: null,
            errorMessage: '',
            errorStack: null,
            originalError: null,
            retryKey: (prevState.retryKey || 0) + 1,
            isRetrying: true
        }), () => {
            if (this.expectedChildren === 0) {
                this.setState({ isRetrying: false });
            }
        });

        if (this._retryTimeout) { clearTimeout(this._retryTimeout); }
        this._retryTimeout = setTimeout(() => {
            this.setState({ isRetrying: false });
        }, 2000);
    };

    handleChildReady = (id) => {
        if (!id) {
            return;
        }
        if (this._retryReadyIds.has(id)) {
            return;
        }
        this._retryReadyIds.add(id);
        const readyCount = this._retryReadyIds.size;
        if (readyCount >= this.expectedChildren) {
            if (this._retryTimeout) { clearTimeout(this._retryTimeout); }
            this.setState({ isRetrying: false });
        }
    };

    getTranslatedErrorMessage = () => {
        // eslint-disable-next-line no-unused-vars
        const { errorCode, originalError } = this.state;

        // Translate based on error code
        switch (errorCode) {
            case 400:
                return translate('ABB.Powertrain.Frontend.errorBoundaryMessageDesc400');
            case 401:
                return translate('ABB.Powertrain.Frontend.errorBoundaryMessageDesc401');
            case 403:
                return translate('ABB.Powertrain.Frontend.errorBoundaryMessageDesc403');
            case 404:
                return translate('ABB.Powertrain.Frontend.errorBoundaryMessageDesc404');
            case 500:
                return translate('ABB.Powertrain.Frontend.errorBoundaryMessageDesc500');
            case 502:
                return translate('ABB.Powertrain.Frontend.errorBoundaryMessageDesc503');
            case 503:
                return translate('ABB.Powertrain.Frontend.errorBoundaryMessageDesc503');
            case 504:
                return translate('ABB.Powertrain.Frontend.errorBoundaryMessageDesc504');
            default:
                // If we have an original error message, try to use it, otherwise use default
                return originalError || translate('ABB.Powertrain.Frontend.errorBoundaryMessageDescUnknown');
        }
    };

    render() {
        if (this.state.hasError) {
            return (
                <ErrorView
                    errorCode={this.state.errorCode}
                    errorMessage={this.getTranslatedErrorMessage()}
                    onRetry={this.handleRetry}
                    onReportIssue={this.props.onReportIssue}
                />
            );
        }

        const { retryKey, isRetrying } = this.state;
        const children = React.Children.map(this.props.children, (child, index) => {
            const id = `child-${index}-${retryKey}`;
            if (React.isValidElement(child)) {
                const key = `${child.key ?? 'child'}-${retryKey}`;
                const cloned = React.cloneElement(child, { key, onErrorBoundaryRetryComplete: () => this.handleChildReady(id) });
                return (
                    <RetryWrapper key={key + '-wrapper'} onMount={() => this.handleChildReady(id)}>
                        {cloned}
                    </RetryWrapper>
                );
            }
            return child;
        });

        if (isRetrying) {
            return <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(255,255,255,0.8)', zIndex: 9999
            }}>
                <LoadingIndicator />
            </div>;
        }

        return (
            <div style={{ position: 'relative', height: '100%' }}>
                {children}
            </div>
        );
    }
}

ErrorBoundaryComponent.propTypes = {
    children: PropTypes.node.isRequired,
    onReportIssue: PropTypes.func
};

export default withTranslation()(ErrorBoundaryComponent);
